// FLAGS: authorization
// Bounds where a completed sign-in may land the user.

/**
 * FLAGS: authorization
 * Returns the value only when it is a safe, app-relative path — guards against
 * open redirects. Returns the parsed, normalised path, so control characters
 * and `..` segments never reach `navigate()`.
 *
 * This resolves the value the way a browser does rather than matching prefixes,
 * because the two disagree: the URL parser strips tabs and newlines, so
 * "/\t/evil.com" survives any `startsWith("//")` test and still resolves to
 * https://evil.com. Anything escaping the base origin is rejected, whatever
 * spelling it arrived in.
 *
 * The base is a fixed sentinel rather than `window.location.origin`: the only
 * question here is whether the reference stays put, which is a property of the
 * string and not of the host the app happens to be served from. It also keeps
 * the helper deterministic under test.
 *
 * Absolute URLs are rejected before parsing, same-origin ones included — the
 * callers only ever have an app path to offer, so accepting a full URL would
 * widen the contract for nothing.
 *
 * The value reaching this guard starts life in the `redirectUri` query
 * parameter and round-trips through sessionStorage across the full-page
 * redirect to Keycloak, so it is the only thing between an attacker-supplied
 * URL and a post-login navigation. Call it on the way in (HomeActions) and
 * again on the way out (AuthRedirectUri): sessionStorage is writable by any
 * script on the origin.
 *
 * OpenUp.Spaces has its own older, prefix-matching copy at
 * src/utils/redirect.ts. It is deliberately NOT changed in step with this one:
 * there the value comes from ProtectedRoute's navigation state and never from
 * the URL, so it is defence in depth rather than a live exposure, and pulling a
 * second app into a Phase Two login fix buys nothing. That copy still has the
 * tab/newline hole described above — treat it as known-weaker, not as a second
 * opinion on what is safe, and prefer promoting this one into @openup/shared
 * over copying it a third time.
 */
const RELATIVE_BASE = "https://openup.invalid";

const getSafeRedirectPath = (value: unknown): string | null => {
  if (typeof value !== "string" || value.length === 0) {
    return null;
  }

  if (!value.startsWith("/")) {
    return null;
  }

  try {
    const url = new URL(value, RELATIVE_BASE);

    if (url.origin !== RELATIVE_BASE) {
      return null;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

export default getSafeRedirectPath;
